import { Button, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Size } from '../../hooks/useModal'
import { PickingRoute } from '../../interfaces/Picking/Picking'
import { AlertBase } from '../../components/_WMS/Alert/AlertBase'
import { DateTime } from 'luxon'
import { useApi } from '../../hooks/useApi'
import { WorkOrderOutbound } from '../../interfaces/WorkOrderOutbound'
import { ApiResponse } from '../../typings/ApiResponse'
import { Status } from '../../hooks/useFetchState'
import { Filter } from '../../components/_WMS/FilterComponent'
import { getWorkOrderOutbounds } from './config/api'
import { WorkOrderState } from '../../interfaces'
import { workOrderForTodayStates, workOrderOverdueStates } from '../../utils/stateHelpers'
import { useConfiguration } from '../../hooks/useConfiguration'
interface WorkOrderOutboundButtonBarProps {
  isPriorizing: boolean
  selectedElement: number[]
  setIsPriorizing: (value: React.SetStateAction<boolean>) => void
  onClickDeleteOpenModalButton: (
    psize?: Size,
    modalState?:
      | {
          workOrderId: number
        }
      | undefined
  ) => void
  onClickCreatePickingRouteButton: (
    psize?: Size,
    modalState?: PickingRoute | undefined
  ) => void
  handleSearch: (filters: Filter[], page?: number, pageSize?: number) => void
  onClickMassiveTagModification: (psize?: Size) => void
  selectedFilters: Filter[]
  setSelectedFilters: any
  workOrderStates: WorkOrderState[] | undefined
}

const overdueFilters = (workOrderStates: WorkOrderState[] | undefined) => {
  const elements = getWorkOrderStatesFilter(
    workOrderOverdueStates,
    workOrderStates
  )

  return [
    {
      key: 'initialDeliveryDate',
      value: DateTime.utc(1970, 1, 1).toFormat('yyyy-MM-dd'),
      label: 'Fecha Entrega Desde',
      displayValue: DateTime.utc(1970, 1, 1).toFormat('yyyy-MM-dd')
    },
    {
      key: 'finalDeliveryDate',
      value: DateTime.local().toFormat('yyyy-MM-dd'),
      label: 'Fecha Entrega Hasta',
      displayValue: DateTime.local().toFormat('yyyy-MM-dd')
    },
    ...elements
  ]
}

const forTodayFilters = (workOrderStates: WorkOrderState[] | undefined) => {
  const todayStart = DateTime.local().startOf('day').toFormat('yyyy-MM-dd')
  const todayEnd = DateTime.local().endOf('day').toFormat('yyyy-MM-dd')

  const workOrderStatesFilter = getWorkOrderStatesFilter(
    workOrderForTodayStates,
    workOrderStates
  )

  return [
    {
      key: 'initialDeliveryDate',
      value: todayStart,
      label: 'Fecha Entrega Desde',
      displayValue: todayStart
    },
    {
      key: 'finalDeliveryDate',
      value: todayEnd,
      label: 'Fecha Entrega Hasta',
      displayValue: todayStart
    },
    ...workOrderStatesFilter
  ]
}

const getWorkOrderStatesFilter = (
  validStates: number[],
  workOrderStates: WorkOrderState[] | undefined
) => {
  return validStates.map((workOrderStateId) => ({
    key: 'workOrderStateId',
    value: workOrderStateId.toString(),
    label: 'Estado',
    displayValue:
      workOrderStates !== undefined && workOrderStates.length > 0
        ? (workOrderStates || []).filter(
            (el) => el.workOrderStateId === workOrderStateId
          )[0].state || ''
        : ''
  }))
}

export const WorkOrderOutboundButtonBar: React.FC<WorkOrderOutboundButtonBarProps> =
  ({
    isPriorizing,
    setIsPriorizing,
    selectedElement,
    onClickCreatePickingRouteButton,
    onClickMassiveTagModification,
    setSelectedFilters,
    workOrderStates
  }) => {
    const { state: StateTodayAlert, callApi: StateTodayAlertCallApi } =
      useApi<ApiResponse<WorkOrderOutbound>>()

    const { state: StateOverdueAlert, callApi: StateOverdueAlertCallApi } =
      useApi<ApiResponse<WorkOrderOutbound>>()

    const requestOverdueWorkOrderOutbound = (filters: Filter[]) => {
      StateOverdueAlertCallApi(getWorkOrderOutbounds(filters))
    }
    const requestTodayWorkOrderOutbound = (filters: Filter[]) => {
      StateTodayAlertCallApi(getWorkOrderOutbounds(filters))
    }

    useEffect(() => {
      requestOverdueWorkOrderOutbound(overdueFilters(workOrderStates))
      requestTodayWorkOrderOutbound(forTodayFilters(workOrderStates))
    }, [])

    const [isAllowedManualPriorization, setIsAllowedManualPriorization] = useState(false)
    const { configurationState, getConfiguration } = useConfiguration()
  
    useEffect(() => {
      if (configurationState === undefined) {
        getConfiguration()
      } else {
        const AutomaticCalculatedPriority =
          configurationState.configuration.filter(
            (el) => el.name === 'AutomaticCalculatedPriority'
          )[0]
  
        if (AutomaticCalculatedPriority !== undefined) {
          setIsAllowedManualPriorization(
            AutomaticCalculatedPriority.value === 'true'
          )
        }
      }
    }, [configurationState])

    return (
      <Grid container justifyContent='center' spacing={2}>
        <Grid container item xs={12} md={4} spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <AlertBase
              status='error'
              title={'Atrasadas'}
              count={StateOverdueAlert.data?.totalItems || 0}
              countDescription='OTs'
              loading={StateOverdueAlert.status === Status.Loading}
              onClick={() =>
                setSelectedFilters(overdueFilters(workOrderStates))
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <AlertBase
              status='warning'
              title={'Para hoy'}
              count={StateTodayAlert.data?.totalItems || 0}
              countDescription='OTs'
              loading={StateTodayAlert.status === Status.Loading}
              onClick={() =>
                setSelectedFilters(forTodayFilters(workOrderStates))
              }
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} md={8} spacing={3} justifyContent='flex-end'>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={3}
            justifyContent='flex-end'
            alignContent='flex-end'
            alignItems='flex-end'
          ></Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={3}
            justifyContent='flex-end'
            alignContent='flex-end'
            alignItems='flex-end'
          >
            <Button
              disabled={selectedElement.length === 0}
              fullWidth
              variant='contained'
              color='primary'
              size='small'
              onClick={() => onClickMassiveTagModification('sm')}
            >
              Modificar TAG
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            lg={3}
            justifyContent='flex-end'
            alignContent='flex-end'
            alignItems='flex-end'
          >
            {' '}
            <Button
              fullWidth
              disabled={selectedElement.length <= 0}
              variant='contained'
              color='primary'
              onClick={() => onClickCreatePickingRouteButton('sm')}
              size='small'
            >
              Crear Ruta Picking
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            lg={3}
            justifyContent='flex-end'
            alignContent='flex-end'
            alignItems='flex-end'
          >
            {!isPriorizing && (
              <Button
                fullWidth
                variant='contained'
                color='primary'
                onClick={() => setIsPriorizing(true)}
                size='small'
                disabled={isAllowedManualPriorization}
              >
                Priorizar
              </Button>
            )}
            {isPriorizing && (
              <Button
                fullWidth
                variant='contained'
                color='primary'
                onClick={() => setIsPriorizing(false)}
                size={'small'}
              >
                Terminar Priorización
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  }
